import { useState, useCallback, useMemo } from 'react'
import { ClubData } from './types'

interface UseEvaluationFiltersProps {
  groupedData: ClubData[]
}

interface UseEvaluationFiltersReturn {
  searchTerm: string
  setSearchTerm: (term: string) => void
  selectedDistricts: string[]
  handleDistrictSelect: (district: string) => void
  selectedRegions: string[]
  handleRegionSelect: (region: string) => void
  selectedEvalStatuses: number[]
  handleEvalStatusSelect: (status: number) => void
  selectedFinalizedYears: string[]
  handleFinalizedYearSelect: (year: string) => void
  selectedEvaluationYears: string[]
  handleEvaluationYearSelect: (year: string) => void
  selectedManualVersions: string[]
  handleManualVersionSelect: (version: string) => void
  clearFilters: () => void
  filteredGroupedData: ClubData[]
}

export const useEvaluationFilters = ({
  groupedData,
}: UseEvaluationFiltersProps): UseEvaluationFiltersReturn => {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([])
  const [selectedRegions, setSelectedRegions] = useState<string[]>([])
  const [selectedEvalStatuses, setSelectedEvalStatuses] = useState<number[]>([])
  const [selectedFinalizedYears, setSelectedFinalizedYears] = useState<
    string[]
  >([])
  const [selectedEvaluationYears, setSelectedEvaluationYears] = useState<
    string[]
  >([])
  const [selectedManualVersions, setSelectedManualVersions] = useState<
    string[]
  >([])

  const handleDistrictSelect = useCallback((district: string) => {
    setSelectedDistricts((prev) =>
      prev.includes(district)
        ? prev.filter((d) => d !== district)
        : [...prev, district]
    )
  }, [])

  const handleRegionSelect = useCallback((region: string) => {
    setSelectedRegions((prev) =>
      prev.includes(region)
        ? prev.filter((r) => r !== region)
        : [...prev, region]
    )
  }, [])

  const handleEvalStatusSelect = useCallback((status: number) => {
    setSelectedEvalStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    )
  }, [])

  const handleFinalizedYearSelect = useCallback((year: string) => {
    setSelectedFinalizedYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    )
  }, [])

  const handleEvaluationYearSelect = useCallback((year: string) => {
    setSelectedEvaluationYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    )
  }, [])

  const handleManualVersionSelect = useCallback((version: string) => {
    setSelectedManualVersions((prev) =>
      prev.includes(version)
        ? prev.filter((v) => v !== version)
        : [...prev, version]
    )
  }, [])

  const clearFilters = useCallback(() => {
    setSelectedDistricts([])
    setSelectedRegions([])
    setSelectedEvalStatuses([])
    setSelectedFinalizedYears([])
    setSelectedEvaluationYears([])
    setSelectedManualVersions([])
  }, [])

  const filteredGroupedData = useMemo(() => {
    let filtered = groupedData

    if (searchTerm) {
      filtered = filtered.filter((club) =>
        club.clubName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectedDistricts.length > 0) {
      filtered = filtered.filter(
        (club) =>
          club.districtName && selectedDistricts.includes(club.districtName)
      )
    }

    if (selectedRegions.length > 0) {
      filtered = filtered.filter(
        (club) =>
          club.districtRegionName &&
          selectedRegions.includes(club.districtRegionName)
      )
    }

    let filteredWithEvalStatus = [...filtered]

    if (selectedEvalStatuses.length > 0) {
      filteredWithEvalStatus = filteredWithEvalStatus.filter((club) => {
        return club.courses.some((course) =>
          course.evaluations.some((evaluation) =>
            selectedEvalStatuses.includes(evaluation.status)
          )
        )
      })

      filteredWithEvalStatus = filteredWithEvalStatus.map((club) => {
        const filteredClub = { ...club }
        filteredClub.courses = filteredClub.courses
          .map((course) => {
            const filteredCourse = { ...course }
            filteredCourse.evaluations = filteredCourse.evaluations.filter(
              (evaluation) => selectedEvalStatuses.includes(evaluation.status)
            )
            return filteredCourse
          })
          .filter((course) => course.evaluations.length > 0)

        return filteredClub
      })
    }

    filtered = filteredWithEvalStatus

    if (selectedFinalizedYears.length > 0) {
      filtered = filtered.filter((club) => {
        return club.courses.some((course) =>
          course.evaluations.some((evaluation) => {
            if (!evaluation.finalizedAt) return false

            const finalizedYear = new Date(evaluation.finalizedAt)
              .getFullYear()
              .toString()
            return selectedFinalizedYears.includes(finalizedYear)
          })
        )
      })

      filtered = filtered.map((club) => {
        const filteredClub = { ...club }
        filteredClub.courses = filteredClub.courses
          .map((course) => {
            const filteredCourse = { ...course }
            filteredCourse.evaluations = filteredCourse.evaluations.filter(
              (evaluation) => {
                if (!evaluation.finalizedAt) return false

                const finalizedYear = new Date(evaluation.finalizedAt)
                  .getFullYear()
                  .toString()
                return selectedFinalizedYears.includes(finalizedYear)
              }
            )
            return filteredCourse
          })
          .filter((course) => course.evaluations.length > 0)

        return filteredClub
      })
    }

    if (selectedEvaluationYears.length > 0) {
      filtered = filtered.filter((club) => {
        return club.courses.some((course) =>
          course.evaluations.some((evaluation) => {
            if (!evaluation.evaluationDate) return false

            const evaluationYear = new Date(evaluation.evaluationDate)
              .getFullYear()
              .toString()
            return selectedEvaluationYears.includes(evaluationYear)
          })
        )
      })

      filtered = filtered.map((club) => {
        const filteredClub = { ...club }
        filteredClub.courses = filteredClub.courses
          .map((course) => {
            const filteredCourse = { ...course }
            filteredCourse.evaluations = filteredCourse.evaluations.filter(
              (evaluation) => {
                if (!evaluation.evaluationDate) return false

                const evaluationYear = new Date(evaluation.evaluationDate)
                  .getFullYear()
                  .toString()
                return selectedEvaluationYears.includes(evaluationYear)
              }
            )
            return filteredCourse
          })
          .filter((course) => course.evaluations.length > 0)

        return filteredClub
      })
    }

    if (selectedManualVersions.length > 0) {
      filtered = filtered.filter((club) => {
        return club.courses.some((course) =>
          course.evaluations.some((evaluation) => {
            if (!evaluation.manualVersion) return false

            const manualVersion = evaluation.manualVersion.toString()
            return selectedManualVersions.includes(manualVersion)
          })
        )
      })

      filtered = filtered.map((club) => {
        const filteredClub = { ...club }
        filteredClub.courses = filteredClub.courses
          .map((course) => {
            const filteredCourse = { ...course }
            filteredCourse.evaluations = filteredCourse.evaluations.filter(
              (evaluation) => {
                if (!evaluation.manualVersion) return false

                const manualVersion = evaluation.manualVersion.toString()
                return selectedManualVersions.includes(manualVersion)
              }
            )
            return filteredCourse
          })
          .filter((course) => course.evaluations.length > 0)

        return filteredClub
      })
    }

    filtered = filtered.filter((club) => club.courses.length > 0)

    return filtered
  }, [
    groupedData,
    searchTerm,
    selectedDistricts,
    selectedRegions,
    selectedEvalStatuses,
    selectedFinalizedYears,
    selectedEvaluationYears,
    selectedManualVersions,
  ])

  return {
    searchTerm,
    setSearchTerm,
    selectedDistricts,
    handleDistrictSelect,
    selectedRegions,
    handleRegionSelect,
    selectedEvalStatuses,
    handleEvalStatusSelect,
    selectedFinalizedYears,
    handleFinalizedYearSelect,
    selectedEvaluationYears,
    handleEvaluationYearSelect,
    selectedManualVersions,
    handleManualVersionSelect,
    clearFilters,
    filteredGroupedData,
  }
}
